import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';

import { routes } from '../app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { loginRequest, msalConfig, requestScopes } from './auth.config';
import { environment } from '../../environments/environment';
import { CustomRouteReuseStrategy } from './custom-route-reuse.strategy';
import { firstValueFrom } from 'rxjs';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding()),
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideHttpClient(withInterceptorsFromDi()),
    // Initialize the MSAL instance
    {
      provide: MSAL_INSTANCE,
      useFactory: () => new PublicClientApplication(msalConfig),
    },
    provideAppInitializer(async () => {
      const msalInstance = inject(MSAL_INSTANCE);
      const msalService = inject(MsalService);

      await msalInstance.initialize();

      try {
        const response = await firstValueFrom(msalService.handleRedirectObservable());
        if (response) {
          msalService.instance.setActiveAccount(response.account);
        }
      } catch (error) {
        console.error('MSAL initialization redirect handling failed:', error);
        // TODO: should something be done here?
      }
    }),
    // Route guard configurations
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: (): MsalGuardConfiguration => ({
        interactionType: InteractionType.Redirect, // Automatically handles login via redirect
        authRequest: loginRequest,
      }),
    },
    // Core MSAL Angular services
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    // Send jwt token with requests to relevant urls
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: () => ({
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          [`${environment.API_BASE_URL}/*`, requestScopes],
          ['https://graph.microsoft.com/v1.0/me*', ['user.read']],
        ]),
      }),
    },
    provideTanStackQuery(new QueryClient()),
  ],
};
